import { useCallback } from "react";
import { useAppDispatch } from "../../app/hooks";
import { isString } from "../../app/utils";
import {
	clearDistrict,
	clearSchool,
	setAdminSessionSynced,
	setDistrict,
	setSchool,
	UserRole,
} from "../../app/userSlice";
import NamedEntity from "../../interfaces/NamedEntity";
import { useMakeSchoolSelectionMutation } from "../schools/schoolsApiSlice";
import { useMakeDistrictSelectionMutation } from "../districts/districtsApiSlice";

export interface AdminSessionUser {
	role: UserRole | null;
	school?: NamedEntity | null;
	district?: NamedEntity | null;
}

/**
 * Pushes the admin's school/district into the server-side session.
 *
 * Several backend endpoints read _userSession.School / _userSession.District as
 * their only source (e.g. User/Create?role=Proctor fills the School field from
 * _userSession.School alone). Those values are written only by
 * SignIn/MakeSchoolSelection and SignIn/MakeDistrictSelection, while GetSelf
 * masks an empty session by falling back to the admin's only school/district
 * from the database without writing it back. So an admin with exactly one
 * school/district looks correctly scoped in the SPA while the server session is
 * still empty. This is the one place that closes that gap.
 *
 * Callers must DEFER this until the user has cleared the terms of service and
 * any forced password change - see hasPendingOnboarding. Until then the
 * backend's RequireConfirmedAccount filter rejects the selection call, so
 * pushing early would just burn a 403 and clear the school for no reason.
 *
 * The returned promise always resolves, and always leaves isAdminSessionSynced
 * true, so the route guards that wait on it can never deadlock. On failure the
 * school/district is cleared, which is what sends the admin to the picker via
 * RequireAdminSession.
 */
export function useSyncAdminSession() {
	const dispatch = useAppDispatch();

	const [makeSchoolSelection] = useMakeSchoolSelectionMutation();
	const [makeDistrictSelection] = useMakeDistrictSelectionMutation();

	return useCallback(
		async (user: AdminSessionUser): Promise<void> => {
			try {
				// MakeSchoolSelection is Principal-only and MakeDistrictSelection is
				// DistrictAdmin-only server-side, so the role gating below is required -
				// calling the other one returns a 403.
				if (user.role === UserRole.SchoolAdmin) {
					if (!user.school) {
						dispatch(clearSchool());
						return;
					}

					try {
						// The response is the school's district, and is the only way a
						// school admin's district gets set - they can't call
						// MakeDistrictSelection themselves.
						const district = await makeSchoolSelection(user.school.id).unwrap();
						dispatch(setSchool(user.school));
						dispatch(
							setDistrict(district ? { id: district.id, name: district.name } : null)
						);
					} catch (error: any) {
						const e = isString(error) ? error : JSON.stringify(error);
						console.log(`error calling MakeSchoolSelection, error = ${e}`);
						dispatch(clearSchool());
					}
					return;
				}

				if (user.role === UserRole.DistrictAdmin) {
					if (!user.district) {
						dispatch(clearDistrict());
						return;
					}

					try {
						await makeDistrictSelection(user.district.id).unwrap();
						dispatch(setDistrict(user.district));
					} catch (error: any) {
						const e = isString(error) ? error : JSON.stringify(error);
						console.log(`error calling MakeDistrictSelection, error = ${e}`);
						dispatch(clearDistrict());
					}
					return;
				}

				// Every other role makes no selection call. For teachers and students that
				// is deliberate, not an oversight - they are scoped by course
				// (MakeCourseSelection sets _userSession.Course, their course list spans
				// every school they belong to, and the backend derives school context from
				// course.School), and they can belong to several schools, so school here is
				// display only. Super and state admins have no session school/district at
				// all. Private mirrors GetSelf's values into redux either way.
			} finally {
				dispatch(setAdminSessionSynced(true));
			}
		},
		[dispatch, makeSchoolSelection, makeDistrictSelection]
	);
}
